import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Zap, Check, Truck, Image as ImageIcon, AlertTriangle, Map, List } from 'lucide-react';
import { resolveImage } from '../../lib/images';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusOf = (site) => {
  if (site.status !== 'available') return 'out';
  if (site.booked) return 'booked';
  return 'available';
};

// List-view row card: full-width, all details visible, tappable
const SpotRow = ({ site, isSelected, onClick }) => {
  const s = statusOf(site);
  const disabled = s !== 'available';
  let theme = '';
  if (disabled) {
    theme = 'bg-[#050814]/60 border-[#B8860B]/10 opacity-60 cursor-not-allowed';
  } else if (isSelected) {
    theme = 'bg-[#B8860B]/15 border-[#B8860B] shadow-[0_0_18px_rgba(184,134,11,0.25)] cursor-pointer';
  } else {
    theme = 'bg-[#050814] border-[#B8860B]/25 hover:border-[#B8860B]/60 hover:bg-[#0B132B] cursor-pointer';
  }
  return (
    <button
      type="button"
      data-testid={`rv-spot-row-${site.number}`}
      onClick={disabled ? undefined : () => onClick(site)}
      disabled={disabled}
      className={`w-full rounded-lg p-4 border-2 transition-all duration-200 text-left flex items-center gap-4 ${theme}`}
    >
      {/* Big site number */}
      <div className="w-14 h-14 flex-shrink-0 rounded-full bg-[#0B132B] border-2 border-[#B8860B]/50 flex items-center justify-center">
        <span className="font-display text-2xl font-bold text-[#B8860B]">#{site.number}</span>
      </div>

      {/* Details */}
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-[#F4F1EA] text-base leading-tight truncate">
          {site.name}
        </p>
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[#F4F1EA]/65">
          <span className="inline-flex items-center gap-1">
            <Zap size={11} className="text-[#D4AF37]" /> {site.amp_service}
          </span>
          {site.big_rig_ok && (
            <span className="inline-flex items-center gap-1">
              <Truck size={11} className="text-[#D4AF37]" /> Big rig
            </span>
          )}
        </div>
      </div>

      {/* Status + checkmark */}
      <div className="flex flex-col items-end gap-1 flex-shrink-0">
        {s === 'available' && !isSelected && (
          <span className="text-[10px] font-bold uppercase tracking-wider text-[#B8860B] bg-[#B8860B]/15 border border-[#B8860B]/40 px-2 py-0.5 rounded">
            Available
          </span>
        )}
        {s === 'booked' && (
          <span className="text-[10px] font-bold uppercase tracking-wider text-red-400 bg-red-500/10 border border-red-500/40 px-2 py-0.5 rounded">
            Booked
          </span>
        )}
        {s === 'out' && (
          <span className="text-[10px] font-bold uppercase tracking-wider text-[#F4F1EA]/50 bg-[#F4F1EA]/5 border border-[#F4F1EA]/20 px-2 py-0.5 rounded">
            Unavailable
          </span>
        )}
        {isSelected && (
          <span className="w-6 h-6 rounded-full bg-[#B8860B] flex items-center justify-center">
            <Check size={14} className="text-[#050814]" />
          </span>
        )}
      </div>
    </button>
  );
};

const Spot = ({ site, isSelected, onClick }) => {
  const s = statusOf(site);
  const disabled = s !== 'available';
  const base = 'relative rounded-lg p-3 transition-all duration-200 border-2 text-left h-full flex flex-col';
  let theme = '';
  if (disabled) {
    theme = 'bg-[#050814]/60 border-[#B8860B]/10 opacity-50 cursor-not-allowed';
  } else if (isSelected) {
    theme = 'bg-[#B8860B]/30 border-[#B8860B] shadow-lg shadow-[#B8860B]/20 cursor-pointer';
  } else {
    theme = 'bg-[#0B132B] border-[#B8860B]/30 hover:border-[#B8860B] hover:bg-[#050814] cursor-pointer';
  }
  return (
    <button
      type="button"
      data-testid={`rv-spot-${site.number}`}
      onClick={disabled ? undefined : () => onClick(site)}
      disabled={disabled}
      className={`${base} ${theme}`}
    >
      <div className="flex items-start justify-between mb-1">
        <span className="text-2xl font-bold text-[#B8860B]">#{site.number}</span>
        {isSelected && (
          <span className="w-6 h-6 rounded-full bg-[#B8860B] flex items-center justify-center">
            <Check size={14} className="text-[#050814]" />
          </span>
        )}
      </div>
      <p className="text-sm font-semibold text-[#F4F1EA] leading-tight">{site.name}</p>
      <div className="mt-1 flex items-center gap-1 text-xs text-[#F4F1EA]/60">
        <Zap size={10} /> {site.amp_service}
        {site.big_rig_ok && (
          <>
            <span className="mx-1">·</span>
            <Truck size={10} /> Big rig
          </>
        )}
      </div>
      <div className="mt-auto pt-2">
        {s === 'available' && (
          <span className="inline-block text-[10px] font-bold uppercase tracking-wider text-[#B8860B] bg-[#B8860B]/15 border border-[#B8860B]/40 px-1.5 py-0.5 rounded">
            Available
          </span>
        )}
        {s === 'booked' && (
          <span className="inline-block text-[10px] font-bold uppercase tracking-wider text-red-400 bg-red-500/10 border border-red-500/40 px-1.5 py-0.5 rounded">
            Booked
          </span>
        )}
        {s === 'out' && (
          <span className="inline-block text-[10px] font-bold uppercase tracking-wider text-[#F4F1EA]/50 bg-[#F4F1EA]/5 border border-[#F4F1EA]/20 px-1.5 py-0.5 rounded">
            Unavailable
          </span>
        )}
      </div>
    </button>
  );
};

export const RvSitePicker = ({ checkIn, checkOut, selectedSiteId, onSelect }) => {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  // Default to list on mobile, map on tablet+ — respects user's screen
  const [view, setView] = useState(() =>
    typeof window !== 'undefined' && window.innerWidth < 768 ? 'list' : 'map'
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (checkIn) params.append('check_in', checkIn.toISOString());
      if (checkOut) params.append('check_out', checkOut.toISOString());
      const { data } = await axios.get(`${API_URL}/api/sites/rv${params.toString() ? `?${params}` : ''}`);
      setSites(data.sites || []);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to load RV sites');
    } finally {
      setLoading(false);
    }
  }, [checkIn, checkOut]);

  useEffect(() => {
    load();
  }, [load]);

  const selected = sites.find((s) => s.id === selectedSiteId);

  // Group sites by grid_y row (top-down map). Expect 2 rows: top (y=1) = spots 1-4, bottom (y=2) = spots 5-8.
  const rows = {};
  sites.forEach((s) => {
    const y = s.grid_y || 1;
    if (!rows[y]) rows[y] = [];
    rows[y].push(s);
  });
  const sortedRows = Object.keys(rows)
    .sort((a, b) => Number(a) - Number(b))
    .map((y) => rows[y].sort((a, b) => (a.grid_x || 0) - (b.grid_x || 0)));

  return (
    <Card
      data-testid="rv-site-picker-card"
      className="border border-[#B8860B]/30 shadow-xl bg-[#0B132B]"
    >
      <CardHeader>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <CardTitle className="text-2xl text-[#F4F1EA]">Pick Your RV Site</CardTitle>
            <p className="text-sm text-[#F4F1EA]/70 mt-1">
              {checkIn && checkOut
                ? 'Availability shown for your selected dates. Tap a site to choose it.'
                : 'Select your dates first to see live availability.'}
            </p>
          </div>
          {/* View toggle */}
          <div
            data-testid="rv-view-toggle"
            role="tablist"
            aria-label="RV site view"
            className="inline-flex rounded-full border border-[#B8860B]/30 bg-[#050814] p-1 flex-shrink-0"
          >
            <button
              type="button"
              role="tab"
              aria-selected={view === 'map'}
              data-testid="rv-view-map-btn"
              onClick={() => setView('map')}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium uppercase tracking-wider transition-colors ${
                view === 'map'
                  ? 'bg-[#B8860B] text-[#050814]'
                  : 'text-[#F4F1EA]/70 hover:text-[#E5C158]'
              }`}
            >
              <Map size={13} /> Map
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={view === 'list'}
              data-testid="rv-view-list-btn"
              onClick={() => setView('list')}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium uppercase tracking-wider transition-colors ${
                view === 'list'
                  ? 'bg-[#B8860B] text-[#050814]'
                  : 'text-[#F4F1EA]/70 hover:text-[#E5C158]'
              }`}
            >
              <List size={13} /> List
            </button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading && <p className="text-[#F4F1EA]/60 text-center py-6">Loading the map...</p>}
        {err && (
          <div className="flex items-center gap-2 text-red-300 bg-red-900/20 border border-red-500/40 rounded p-3 mb-4">
            <AlertTriangle size={16} /> <span>{err}</span>
          </div>
        )}

        {/* ================= LIST VIEW ================= */}
        {!loading && sites.length > 0 && view === 'list' && (
          <div data-testid="rv-list-view" className="space-y-3 mb-5">
            {sites
              .slice()
              .sort((a, b) => (a.number || 0) - (b.number || 0))
              .map((site) => (
                <SpotRow
                  key={site.id}
                  site={site}
                  isSelected={selectedSiteId === site.id}
                  onClick={onSelect}
                />
              ))}
          </div>
        )}

        {/* ================= MAP VIEW ================= */}
        {/* Top-down satellite-style map of the ranch (matches printed site map) */}
        {!loading && sites.length > 0 && view === 'map' && (
          <>
            {/* Mobile scroll hint */}
            <p className="md:hidden text-[10px] uppercase tracking-[0.28em] text-[#F4F1EA]/50 mb-2 text-center">
              ← swipe to explore the map →
            </p>

            <div
              data-testid="rv-site-map"
              className="rounded-lg border border-[#B8860B]/20 mb-5 bg-[#7a5a07] overflow-hidden"
            >
              {/* Horizontally scrollable area — mobile scrolls, desktop fits */}
              <div className="relative overflow-x-auto overflow-y-hidden scrollbar-thin">
                {/* Fade edges on mobile only, hinting at horizontal overflow */}
                <div
                  aria-hidden="true"
                  className="md:hidden pointer-events-none absolute inset-y-0 left-0 w-5 bg-gradient-to-r from-[#7a5a07] to-transparent z-10"
                />
                <div
                  aria-hidden="true"
                  className="md:hidden pointer-events-none absolute inset-y-0 right-0 w-5 bg-gradient-to-l from-[#7a5a07] to-transparent z-10"
                />

                {/* Grid: [vertical road] [bar/grass column] [spot 1/5] [spot 2/6] [spot 3/7] [spot 4/8]
                    min-width keeps the layout from squishing on mobile — scrolls horizontally instead. */}
                <div
                  className="grid min-w-[720px] md:min-w-0"
                  style={{
                    gridTemplateColumns: '14px 1.4fr 1fr 1fr 1fr 1fr',
                    gridTemplateRows: 'minmax(120px, auto) 18px minmax(120px, auto)',
                    columnGap: '12px',
                    rowGap: '0',
                    padding: '12px 14px 14px 0',
                  }}
                >
              {/* Vertical road on far left, spans all rows */}
              <div
                style={{ gridColumn: '1 / 2', gridRow: '1 / 4' }}
                className="bg-[#1a2547] relative"
                title="Road"
              >
                <span
                  data-testid="rv-map-entrance-label"
                  className="absolute top-2 left-1/2 -translate-x-1/2 text-[8px] uppercase tracking-[0.2em] text-[#B8860B] whitespace-nowrap [writing-mode:vertical-rl] rotate-180"
                >
                  ↓ Entrance
                </span>
                <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 -rotate-90 text-[9px] uppercase tracking-[0.25em] text-[#F4F1EA]/55 whitespace-nowrap">
                  Road
                </span>
              </div>

              {/* Bar & Deck — top-left amenity slot */}
              <div
                style={{ gridColumn: '2 / 3', gridRow: '1 / 2' }}
                className="bg-[#050814] border-2 border-[#B8860B] rounded flex items-center justify-center"
              >
                <div className="text-center px-2">
                  <p className="text-xs font-bold text-[#B8860B] leading-tight">Bar &amp; Deck</p>
                  <p className="text-[9px] text-[#F4F1EA]/55 mt-0.5">Amenity</p>
                </div>
              </div>

              {/* Top row spots — placed in columns 3..6 */}
              {(sortedRows[0] || []).slice(0, 4).map((site, i) => (
                <div
                  key={site.id}
                  style={{ gridColumn: `${3 + i} / ${4 + i}`, gridRow: '1 / 2' }}
                >
                  <Spot
                    site={site}
                    isSelected={selectedSiteId === site.id}
                    onClick={onSelect}
                  />
                </div>
              ))}

              {/* Horizontal road between rows (spans bar+spot columns) */}
              <div
                style={{ gridColumn: '2 / 7', gridRow: '2 / 3' }}
                className="bg-[#1a2547] relative my-1"
              >
                <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-[9px] uppercase tracking-[0.3em] text-[#F4F1EA]/55">
                  Road
                </span>
              </div>

              {/* Empty grass under the bar (bottom-left) */}
              <div
                style={{ gridColumn: '2 / 3', gridRow: '3 / 4' }}
                className="bg-[#D4AF37] rounded"
              />

              {/* Bottom row spots */}
              {(sortedRows[1] || []).slice(0, 4).map((site, i) => (
                <div
                  key={site.id}
                  style={{ gridColumn: `${3 + i} / ${4 + i}`, gridRow: '3 / 4' }}
                >
                  <Spot
                    site={site}
                    isSelected={selectedSiteId === site.id}
                    onClick={onSelect}
                  />
                </div>
              ))}
                </div>
              </div>

              {/* Creek label at the bottom — outside scroll area so it's always visible */}
              <div className="flex items-center justify-end px-4 py-2 bg-[#5c4205] border-t border-[#B8860B]/15">
                <span className="text-[10px] uppercase tracking-[0.3em] text-[#F4F1EA]/55">
                  ≈ Creek bottom ≈
                </span>
              </div>
            </div>
          </>
        )}

        {/* Selected site details */}
        {selected && (
          <div
            data-testid="rv-site-selected-details"
            className="bg-[#050814] border border-[#B8860B]/40 rounded-lg p-4 flex gap-4"
          >
            <div className="w-28 h-28 flex-shrink-0 rounded bg-[#0B132B] border border-[#B8860B]/20 overflow-hidden flex items-center justify-center">
              {selected.image_url ? (
                <img src={resolveImage(selected.image_url)} alt={`Site ${selected.number}`} className="w-full h-full object-cover" />
              ) : (
                <ImageIcon size={32} className="text-[#B8860B]/40" />
              )}
            </div>
            <div className="flex-1">
              <p className="text-xs uppercase tracking-wider text-[#B8860B]/80 mb-1">Selected site</p>
              <h4 className="text-lg font-bold text-[#F4F1EA]">
                #{selected.number} — {selected.name}
              </h4>
              <p className="text-sm text-[#F4F1EA]/80 mt-1">{selected.description}</p>
              <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-[#F4F1EA]/70">
                <span className="inline-flex items-center gap-1">
                  <Zap size={12} className="text-[#B8860B]" /> {selected.amp_service}
                </span>
                {selected.big_rig_ok && (
                  <span className="inline-flex items-center gap-1">
                    <Truck size={12} className="text-[#B8860B]" /> Big rig friendly
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Legend */}
        <div className="mt-4 flex flex-wrap gap-4 text-xs text-[#F4F1EA]/60">
          <span className="inline-flex items-center gap-1">
            <span className="w-3 h-3 rounded bg-[#0B132B] border-2 border-[#B8860B]/30"></span> Available
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="w-3 h-3 rounded bg-[#B8860B]/30 border-2 border-[#B8860B]"></span> Selected
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="w-3 h-3 rounded bg-[#050814]/60 border-2 border-[#B8860B]/10"></span> Booked / Unavailable
          </span>
        </div>
      </CardContent>
    </Card>
  );
};

export default RvSitePicker;
